import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { t } from '@lingui/macro';
import { useApolloClient } from '@apollo/client';

import ArrowLeft from '@decisiv/iconix/lib/components/ArrowLeft';
import ArrowRight from '@decisiv/iconix/lib/components/ArrowRight';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Loading from '@decisiv/ui-components/lib/components/Loading';
import Wizard from '@decisiv/ui-components/lib/components/Wizard';

import useOpenBulkImport from 'hooks/useOpenBulkImport';

import { type ResetTarget } from './types';
import { useApplyDepotResetMutation } from './hooks/useApplyDepotResetMutation';
import { usePlanDepotResetMutation } from './hooks/usePlanDepotResetMutation';
import { usePollDepotReset } from './hooks/usePollDepotReset';
import { clearInFlightRun, saveInFlightRun } from './utils/inFlightRunStorage';
import { parseResetDepotCsv } from './utils/parseResetDepotCsv';
import {
  validateResetDepotFile,
  type ResetDepotFileError,
} from './utils/validateResetDepotFile';
import ReviewStep from './ReviewStep';
import SummaryStep from './SummaryStep';
import UploadStep from './UploadStep';

const UPLOAD_STEP = 1;
const REVIEW_STEP = 2;
const SUMMARY_STEP = 3;

// PLANNING/APPLYING render as a loading state of the *next* step (Review or
// Summary), so activeStep can derive from status alone with no separate
// "in-between" step.
const STEP_FOR_STATUS: Record<DepotResetStatusEnum, number> = {
  PLANNING: REVIEW_STEP,
  PLANNED: REVIEW_STEP,
  APPLYING: SUMMARY_STEP,
  COMPLETED: SUMMARY_STEP,
  FAILED: SUMMARY_STEP,
};

// Used only when the poll gives up having never received any response, so
// FAILED's "not applied" copy is accurate. Not used once a status has been
// seen, or for an apply rejection -- see summaryContent.
const failedRunStandIn = () => ({
  id: '',
  status: 'FAILED' as const,
  outcome: 'FAILURE' as const,
  errorMessage: undefined,
  reportUrl: undefined,
  addCount: 0,
  transferCount: 0,
  updateCount: 0,
  releaseCount: 0,
  noopCount: 0,
  invalidCount: 0,
  depotSummaries: [],
  excludedDepots: [],
});

export interface ResetDepotAssetsModalProps {
  /**
   * What this reset acts on. Always describes the page the wizard is open on,
   * which may differ from what a recovered run was planned with -- harmless,
   * since a re-plan after Back runs on this same page.
   */
  target: ResetTarget;
  /**
   * The platform account this run belongs to, from the route -- NOT derived
   * from `target` (a recovered run's target may not even carry depot ids).
   * Passed straight through to `saveInFlightRun` so a later
   * `readInFlightRun(routeAccountId)` can actually match it.
   */
  accountId: string;
  /** Set when recovering an interrupted run; skips the upload step. */
  resumeId?: string;
  onClose: () => void;
}

export default function ResetDepotAssetsModal({
  target,
  accountId,
  resumeId,
  onClose,
}: ResetDepotAssetsModalProps) {
  const client = useApolloClient();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<ResetDepotFileError | null>(null);
  const [validating, setValidating] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [planDepotReset] = usePlanDepotResetMutation();
  const [applyDepotReset] = useApplyDepotResetMutation();
  const {
    startPolling,
    depotReset,
    pollFailed,
    reset: resetPoll,
  } = usePollDepotReset();

  // Every parent mounts this modal only while open, so unmounting releases the
  // id and lets the app-level toast report a reset that finishes afterwards.
  // A resumed run is claimed from mount, and the claim is dropped once the
  // modal has lost track of it, since the summary then shows no outcome.
  useOpenBulkImport(
    pollFailed || applyError ? undefined : depotReset?.id ?? resumeId,
  );

  useEffect(() => {
    if (resumeId) startPolling(resumeId);
  }, [resumeId, startPolling]);

  useEffect(() => {
    // The old plan-time rejection described the file that was just replaced,
    // so leaving it up would sit a stale rejection message directly above a
    // freshly-picked filename.
    setServerError(null);

    if (!uploadedFile) {
      setFileError(null);
      return undefined;
    }

    let cancelled = false;
    setValidating(true);
    validateResetDepotFile(uploadedFile).then((error) => {
      if (cancelled) return;
      setFileError(error);
      setValidating(false);
    });

    return () => {
      cancelled = true;
    };
  }, [uploadedFile]);

  const status = depotReset?.status;
  const isBusy = status === 'PLANNING' || status === 'APPLYING';
  // Checked before isBusy in renderActions: pollFailed can be true while
  // status is still PLANNING/APPLYING, and busy-first would leave zero
  // enabled actions.
  const isTerminal =
    status === 'COMPLETED' || status === 'FAILED' || pollFailed || !!applyError;

  // Two ways of losing track, both keeping the stored id (see handleClose): a
  // poll that gave up on a run which had already reported a status, or an
  // apply rejection -- neither proves the server never got the request.
  const lostTrackOfRun = (pollFailed && !!depotReset) || !!applyError;

  // `submitting` clears when `status` CHANGES, not on busy/terminal: the plan
  // path can reach PLANNED without ever observing PLANNING (a read-replica
  // race), so "busy or terminal" would never fire. The stale post-apply
  // PLANNED poll must not count as a change either, or a double-submit window
  // reopens. Worst case this is bounded by MAX_ATTEMPTS x POLL_INTERVAL_MS
  // (~60 minutes, not one interval), and Escape still closes the modal since
  // onCancel is withheld only while isBusy.
  useEffect(() => {
    setSubmitting(false);
  }, [status, pollFailed]);

  useEffect(() => {
    if (!isTerminal) return;
    client.refetchQueries({
      include: ['ManagedDepotsFromPlatformAccount', 'ActivityLogs'],
    });
  }, [isTerminal, client]);

  const handlePlan = useCallback(async () => {
    /* istanbul ignore if -- @preserve: defensive only. Continue is disabled
     * whenever uploadedFile is null, and a disabled button never fires
     * onClick, so this handler is never actually invoked without a file. */
    if (!uploadedFile) return;

    setSubmitting(true);
    setServerError(null);
    try {
      const parsedRows = await parseResetDepotCsv(uploadedFile);
      // Bridges parseResetDepotCsv's `string | null` fields to
      // DepotResetRowInput's `string | undefined` (both types predate this
      // change, so translated here instead of altering either).
      const rows: DepotResetRowInput[] = parsedRows.map((row) => ({
        rowNumber: row.rowNumber,
        fmsId: row.fmsId ?? undefined,
        vin: row.vin ?? undefined,
        unitNumber: row.unitNumber ?? undefined,
        mileage: row.mileage,
        engineHours: row.engineHours,
      }));
      const scopeVariables =
        target.scope === 'account'
          ? { platformAccountId: accountId }
          : { depotIds: target.depotIds };
      const { data } = await planDepotReset({
        variables: { ...scopeVariables, rows },
      });

      const id = data?.planDepotReset?.depotReset?.id;
      /* istanbul ignore if -- @preserve */
      if (!id) throw new Error(t`Could not start the depot reset.`);

      saveInFlightRun(id, accountId);
      startPolling(id);
      // Not cleared here: `status` hasn't changed yet, so clearing now would
      // re-enable Continue for a duplicate plan. The effect above releases it.
    } catch (error) {
      // Plan-time rejections are top-level GraphQL errors, already written as
      // user-facing sentences. Surfaced verbatim rather than reworded.
      setServerError((error as Error).message);
      setSubmitting(false);
    }
  }, [uploadedFile, target, accountId, planDepotReset, startPolling]);

  const handleApply = useCallback(async () => {
    /* istanbul ignore if -- @preserve: defensive only. onNext only resolves to
     * handleApply once activeStep has left the upload step, which requires a
     * status derived from a non-null depotReset. */
    if (!depotReset) return;

    setSubmitting(true);
    try {
      await applyDepotReset({ variables: { id: depotReset.id } });
      // The returned reset is still PLANNED (APPLYING is set async by the
      // worker); passing 'PLANNED' keeps usePollDepotReset from treating that
      // stale first response as already resting.
      startPolling(depotReset.id, 'PLANNED');
      // Not cleared here either: the row is still PLANNED, so this would
      // re-enable a duplicate apply. Released by the effect above.
    } catch (error) {
      // Apply rejections aren't fixable in place, so they end the run on
      // Summary as an UNKNOWN outcome (see summaryContent) instead of
      // re-enabling Continue. `serverError` can't carry this -- it only
      // renders in UploadStep, not Review. `||` not `??`: an empty-string
      // message is falsy but not nullish, and every gate here is
      // `!!applyError`.
      setApplyError(
        (error as Error).message || t`the server did not return a reason`,
      );
      setSubmitting(false);
    }
  }, [depotReset, applyDepotReset, startPolling]);

  const handleClose = useCallback(() => {
    // Neither a stale give-up nor a rejected apply proves the run is over,
    // and there's no depotResets index query to recover it by -- keep the id
    // so a reload can pick a spurious failure back up.
    if (!lostTrackOfRun) clearInFlightRun();
    onClose();
  }, [onClose, lostTrackOfRun]);

  // Sends the user from Review back to Upload. uploadedFile/fileError are
  // deliberately left untouched: the file is still attached, and its
  // validation result still describes it. resetPoll() clears depotReset (and
  // any pending timer), and clearInFlightRun() drops the stored run id, so
  // Continue plans a fresh run from scratch rather than resuming this one.
  const handleBack = useCallback(() => {
    resetPoll();
    clearInFlightRun();
    setServerError(null);
    setApplyError(null);
  }, [resetPoll]);

  // pollFailed/applyError force Summary regardless of status: a give-up can
  // happen with `status` still undefined (an id the server no longer
  // recognizes), where STEP_FOR_STATUS would otherwise strand activeStep on
  // Upload while the footer has already gone terminal.
  let activeStep = UPLOAD_STEP;
  if (pollFailed || applyError) {
    activeStep = SUMMARY_STEP;
  } else if (status) {
    activeStep = STEP_FOR_STATUS[status];
  }

  const reviewContent = useMemo(() => {
    if (status === 'PLANNING' || !depotReset) {
      return (
        <Loading
          size="medium"
          title={t`Validating VIN(s)...`}
          subtitle={t`Your assets are being processed. This might take a while.`}
        />
      );
    }
    return <ReviewStep depotReset={depotReset} />;
  }, [status, depotReset]);

  const summaryContent = useMemo(() => {
    // No status ever arrived, so there's genuinely nothing to report. Checked
    // first: an apply rejection always has a depotReset (see handleApply).
    if (!depotReset) {
      return <SummaryStep depotReset={failedRunStandIn()} />;
    }
    // A rejected apply is not proof it didn't happen -- the request may have
    // reached the server with only the response lost, so this uses the same
    // outcomeUnknown path as a poll give-up rather than FAILED's "not
    // applied" copy. The kept run id (lostTrackOfRun) self-heals: a genuine
    // rejection leaves the run PLANNED for Continue to retry, a truly-unknown
    // id polls to the FAILED stand-in above, and a lost response shows the
    // real APPLYING/COMPLETED -- bounded by the storage module's 24-hour TTL.
    if (applyError) {
      return (
        <SummaryStep
          depotReset={{
            ...depotReset,
            errorMessage: t`We could not confirm whether this reset started (${applyError}). Reload the page to check on it before uploading another file.`,
          }}
          outcomeUnknown
        />
      );
    }
    // Gave up on a run that HAD reported a status: the real depotReset (its
    // last known counts) is kept rather than substituting an all-zero FAILED.
    if (pollFailed) {
      return <SummaryStep depotReset={depotReset} outcomeUnknown />;
    }
    if (status === 'APPLYING') {
      return (
        <Loading
          size="medium"
          title={t`Resetting depot assets...`}
          subtitle={t`Your assets are being processed. This might take a while.`}
        />
      );
    }
    return <SummaryStep depotReset={depotReset} />;
  }, [status, depotReset, pollFailed, applyError]);

  const steps = useMemo(
    () => [
      {
        title: t`Upload`,
        hideBodyTitle: true,
        content: (
          <UploadStep
            uploadedFile={uploadedFile}
            onFileChange={setUploadedFile}
            fileError={fileError}
            serverError={serverError}
            scope={target.scope}
          />
        ),
      },
      { title: t`Review`, hideBodyTitle: true, content: reviewContent },
      { title: t`Summary`, hideBodyTitle: true, content: summaryContent },
    ],
    [
      uploadedFile,
      fileError,
      serverError,
      target,
      reviewContent,
      summaryContent,
    ],
  );

  // Every branch below carries the same data-testid on its root, so a test can
  // scope "what does the footer offer right now" to this subtree -- the whole
  // document isn't a usable proxy, since UploadStep renders its own always-
  // enabled Upload/Download/Remove buttons in the step body.
  const renderActions = useCallback((): ReactNode => {
    // Checked first -- see the isTerminal definition above.
    if (isTerminal) {
      return (
        <Button
          text={t`Close`}
          onClick={handleClose}
          data-testid="reset-depot-wizard-actions"
        />
      );
    }

    // Cancel is deliberately absent while the server is working: the run cannot
    // be abandoned mid-flight, which is also why polling lives in this modal and
    // needs nothing to outlive it.
    if (isBusy) {
      return (
        <Button
          text={t`Processing`}
          loadingText={t`Processing`}
          loading
          disabled
          data-testid="reset-depot-wizard-actions"
        />
      );
    }

    const onNext = activeStep === UPLOAD_STEP ? handlePlan : handleApply;
    const nextDisabled =
      activeStep === UPLOAD_STEP
        ? !uploadedFile || validating || !!fileError || submitting
        : submitting;

    return (
      <Flex
        flex={1}
        justifyContent="flex-end"
        style={{ gap: '8px' }}
        data-testid="reset-depot-wizard-actions"
      >
        <Button
          text={t`Cancel`}
          kind="secondary"
          variant="ghost"
          onClick={handleClose}
          disabled={submitting}
        />
        {activeStep === REVIEW_STEP && (
          <Button
            text={t`Back`}
            icon={ArrowLeft}
            kind="secondary"
            onClick={handleBack}
            disabled={submitting}
          />
        )}
        <Button
          text={t`Continue`}
          icon={ArrowRight}
          iconPosition="right"
          onClick={onNext}
          disabled={nextDisabled}
          loading={submitting}
          data-testid="reset-depot-continue-button"
        />
      </Flex>
    );
  }, [
    isTerminal,
    isBusy,
    activeStep,
    uploadedFile,
    validating,
    fileError,
    submitting,
    handlePlan,
    handleApply,
    handleClose,
    handleBack,
  ]);

  return (
    <Wizard
      visible
      id="reset-depot-assets-wizard"
      title={t`Reset Depots Assets`}
      steps={steps}
      activeStep={activeStep}
      onCancel={isBusy ? undefined : handleClose}
      renderActions={renderActions}
    />
  );
}
